import React, { useState, useCallback } from 'react';
import SearchPanel from './components/SearchPanel';
import FlightResultsEnhanced from './components/FlightResultsEnhanced';
import PairingMatrix from './components/PairingMatrix';
import ComparativeQuote from './components/ComparativeQuote';
import BookingForm from './components/BookingForm';
import BookingConfirmation from './components/BookingConfirmation';
import ManageBooking from './components/ManageBooking';

// App views: search | results | pairing | compare | booking | confirmation | manage
export default function App() {
  const [view, setView] = useState('search');
  const [activeTab, setActiveTab] = useState('search');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedFlight, setSelectedFlight] = useState(null);
  const [searchParams, setSearchParams] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState(null);

  // Comparative Quote state
  const [quoteOutbound, setQuoteOutbound] = useState([]);
  const [quoteReturn, setQuoteReturn] = useState([]);
  const [quotePairings, setQuotePairings] = useState([]);
  const [showPairingMatrix, setShowPairingMatrix] = useState(false);

  const handleSearchComplete = useCallback((results, params) => {
    setSearchResults(results);
    setSearchParams(params);
    setView('results');
    setError(null);
    // Reset quote state on new search
    setQuoteOutbound([]);
    setQuoteReturn([]);
    setQuotePairings([]);
  }, []);

  // Legacy single-flight selection (still available from enhanced results)
  const handleSelectFlight = useCallback((flight) => {
    setSelectedFlight(flight);
    setView('booking');
  }, []);

  // Step 1: User clicks "Build Comparative Quote" in results
  const handleBuildQuote = useCallback((outbound, returnFlights, allItineraries) => {
    setQuoteOutbound(outbound);
    setQuoteReturn(returnFlights);
    setShowPairingMatrix(true);
  }, []);

  // Step 2: User pairs flights in matrix and clicks "Compare N Options"
  const handleComparePairings = useCallback((pairings) => {
    setQuotePairings(pairings);
    setShowPairingMatrix(false);
    setView('compare');
  }, []);

  // Step 3: User selects an option from the comparative quote
  const handleSelectOption = useCallback((pairing) => {
    // Build a flight object compatible with BookingForm
    const combinedFlight = {
      id: `quote-${Date.now()}`,
      legs: [],
      totalPrice: pairing.perPersonPrice || pairing.combinedPrice,
      currency: 'USD',
      raw: {},
    };

    // Add outbound leg
    if (pairing.outbound) {
      combinedFlight.legs.push({
        direction: 'outbound',
        elapsedTime: pairing.outbound.duration,
        schedules: pairing.outbound.schedules || [{
          departure: { airport: pairing.outbound.departureAirport, time: pairing.outbound.departureTime },
          arrival: { airport: pairing.outbound.arrivalAirport, time: pairing.outbound.arrivalTime },
          carrier: { marketing: pairing.outbound.airlineCode, marketingFlightNumber: pairing.outbound.flightNumber?.replace(/[A-Z]+\s?/i, '') },
        }],
      });
    }

    // Add return leg
    if (pairing.return) {
      combinedFlight.legs.push({
        direction: 'return',
        elapsedTime: pairing.return.duration,
        schedules: pairing.return.schedules || [{
          departure: { airport: pairing.return.departureAirport, time: pairing.return.departureTime },
          arrival: { airport: pairing.return.arrivalAirport, time: pairing.return.arrivalTime },
          carrier: { marketing: pairing.return.airlineCode, marketingFlightNumber: pairing.return.flightNumber?.replace(/[A-Z]+\s?/i, '') },
        }],
      });
    }

    setSelectedFlight(combinedFlight);
    setView('booking');
  }, []);

  const handleBookingComplete = useCallback((result) => {
    setBookingResult(result);
    setView('confirmation');
  }, []);

  const handleNewSearch = useCallback(() => {
    setView('search');
    setSearchResults([]);
    setSelectedFlight(null);
    setBookingResult(null);
    setQuoteOutbound([]);
    setQuoteReturn([]);
    setQuotePairings([]);
    setError(null);
    setActiveTab('search');
  }, []);

  const handleBackToResults = useCallback(() => {
    setView('results');
    setSelectedFlight(null);
  }, []);

  const handleBackToSearch = useCallback(() => {
    setView(searchResults.length > 0 ? 'results' : 'search');
    setQuotePairings([]);
  }, [searchResults]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'search') {
      setView(searchResults.length > 0 ? 'results' : 'search');
    } else if (tab === 'manage') {
      setView('manage');
    }
  };

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-inner">
          <div className="logo">
            <span className="logo-icon">&#9992;</span>
            <div>
              <h1>AirDesk</h1>
              <span>Air Ticketing Workflow</span>
            </div>
          </div>
          <nav className="nav-tabs">
            <button
              className={`nav-tab ${activeTab === 'search' ? 'active' : ''}`}
              onClick={() => handleTabChange('search')}
            >
              Flight Search
            </button>
            <button
              className={`nav-tab ${activeTab === 'manage' ? 'active' : ''}`}
              onClick={() => handleTabChange('manage')}
            >
              Manage Booking
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <div className="container">
        {error && (
          <div className="error-banner">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Search + Results View */}
        {(view === 'search' || view === 'results') && activeTab === 'search' && (
          <>
            <SearchPanel
              onSearchComplete={handleSearchComplete}
              onError={setError}
              initialParams={searchParams}
            />
            {view === 'results' && (
              <FlightResultsEnhanced
                results={searchResults}
                searchParams={searchParams}
                onSelectFlight={handleSelectFlight}
                onBuildQuote={handleBuildQuote}
              />
            )}
          </>
        )}

        {/* Comparative Quote View */}
        {view === 'compare' && (
          <ComparativeQuote
            pairings={quotePairings}
            searchParams={searchParams}
            onBack={handleBackToSearch}
            onSelectOption={handleSelectOption}
          />
        )}

        {/* Booking Form */}
        {view === 'booking' && (
          <BookingForm
            flight={selectedFlight}
            searchParams={searchParams}
            onBookingComplete={handleBookingComplete}
            onBack={handleBackToResults}
            onError={setError}
          />
        )}

        {/* Booking Confirmation */}
        {view === 'confirmation' && (
          <BookingConfirmation
            booking={bookingResult}
            onNewSearch={handleNewSearch}
          />
        )}

        {/* Manage Booking */}
        {view === 'manage' && <ManageBooking onError={setError} />}
      </div>

      {/* Pairing Matrix Modal */}
      {showPairingMatrix && (
        <PairingMatrix
          outboundFlights={quoteOutbound}
          returnFlights={quoteReturn}
          allItineraries={searchResults}
          searchParams={searchParams}
          onCompare={handleComparePairings}
          onClose={() => setShowPairingMatrix(false)}
        />
      )}
    </div>
  );
}
